import AxiosService from '@/api/ApiBase';
import { FilterOptionsParams, FiltersService } from '@/types/service/filters.service';
import { HblEntity } from '@/types/hbl/hbl.entity';
import { HblFilters, type HblTotalsResponse } from '@/types/hbl/hbl.filters';
import { RestoreService } from '@/types/service/restore.service';
import { CreateEmptyHBLPayload } from '@/types/hbl/copies/hbl.copies.payload';
import { RejectionPayload } from '@/types/hbl/pdf/rejection.entity';
import { VirtualSearchPayload, VirtualSearchResponse } from '@/types/virtual-search';

const HOUSE_BASE_URL = 'warehouse/manifest/hbl';

// const HOUSE_BASE_URL = 'http://localhost:3000/api/manifest/hbl';

export interface ReleaseValidationResponse {
  releaseStatus: boolean;
  availableDate: boolean;
  entryNumber: boolean;
  entryDate: boolean;
  customReleased: boolean;
  customReleasedBy: boolean;
  creditHold: boolean;
  customReleasedDate: boolean;
  frieghtReleased: boolean;
  inventoryLocation: boolean;
  cargoHold: boolean;
  markHold: boolean;
  isExchangePalletRetruned: boolean;
  isExchangePalletChargePaid: boolean;
  isDeliveryOrderAvailable: boolean;
  charges: false;
}

export interface HblExchangePalletsPayload {
  hblId: string;
  isExchangePalletResolved: boolean;
  exchangePalletComments: string;
}

export interface HblStatusValidationResponse {
  hblStatus: boolean;
  isPcsValid: boolean;
  isVolumeValid: boolean;
  isWeightValid: boolean;
}

class HouseService implements FiltersService<HblEntity, HblFilters>, RestoreService {
  getAll(mblId: string = '') {
    const params = mblId ? { mblId } : {};
    return AxiosService.get(`${HOUSE_BASE_URL}/all`, {
      params,
    });
  }

  virtualSearch(payload: VirtualSearchPayload<HblEntity>) {
    return AxiosService.post<VirtualSearchResponse<HblEntity> & { totals?: HblTotalsResponse }>(
      'warehouse/v2/manifest/hbl/search',
      payload
    );
  }

  restore(id: string) {
    return AxiosService.put<string>(`${HOUSE_BASE_URL}/restore/${id}`);
  }

  get(id: string) {
    return AxiosService.get(`${HOUSE_BASE_URL}/all/?mblId=${id}`);
  }

  create(data: HblEntity) {
    return AxiosService.post(`${HOUSE_BASE_URL}/create`, data);
  }

  delete(hblId: string) {
    return AxiosService.delete(`${HOUSE_BASE_URL}/delete/${hblId}`);
  }

  getOneById(id: string) {
    return AxiosService.get(`${HOUSE_BASE_URL}/${id}`);
  }

  getFilterOptions<T>(params: Omit<FilterOptionsParams, 'limit'>) {
    return AxiosService.get<T>(`${HOUSE_BASE_URL}/filter/options`, {
      params: {
        ...params,
        limit: 100,
      },
    });
  }

  update(data: Partial<HblEntity>) {
    return AxiosService.put(`${HOUSE_BASE_URL}/update`, data);
  }

  updateLoadAvailability(payload: { hblIds: string[]; isUnableToLoad: boolean }) {
    return AxiosService.put(`${HOUSE_BASE_URL}/update/unableToLoadStatus`, payload);
  }

  getMilestones(id: string) {
    return AxiosService.get(`${HOUSE_BASE_URL}/get/milestones/${id}`);
  }

  getFilteredItems(filters: HblFilters) {
    return AxiosService.post(`${HOUSE_BASE_URL}/get/filter`, filters);
  }

  markLoaded(hblIds: string[] | string, isLoaded: boolean) {
    return AxiosService.put(`${HOUSE_BASE_URL}/update/loadStatus`, {
      hblIds,
      isLoaded,
    });
  }

  validateStatus(id: string) {
    return AxiosService.get<HblStatusValidationResponse>(`${HOUSE_BASE_URL}/validate/status`, {
      params: {
        manifestId: id,
      },
    });
  }

  exchangePalletsResolved(payload: HblExchangePalletsPayload) {
    return AxiosService.put<{ msg: string; releasedNotes: string }>(
      `${HOUSE_BASE_URL}/update/exchangePalletResolved`,
      payload
    );
  }

  validateReleaseStatus(id: string) {
    return AxiosService.get<ReleaseValidationResponse>(`${HOUSE_BASE_URL}/validate/releaseStatus`, {
      params: {
        manifestId: id,
      },
    });
  }

  forceRelease(id: string) {
    return AxiosService.put<HblEntity>(`${HOUSE_BASE_URL}/force-release/${id}`);
  }

  controlShipment(id: string) {
    return AxiosService.put<HblEntity>(`${HOUSE_BASE_URL}/control-shipment/${id}`);
  }

  createEmptyHBL(payload: CreateEmptyHBLPayload) {
    return AxiosService.post(`${HOUSE_BASE_URL}/copies`, payload);
  }

  createRejectionNotice(payload: RejectionPayload) {
    return AxiosService.put<HblEntity>(`${HOUSE_BASE_URL}/rejectionNotice`, payload);
  }

  removeRejectionNotice(manifestId: string) {
    return AxiosService.put<HblEntity>(`${HOUSE_BASE_URL}/removeRejectionNotice`, { manifestId });
  }

  getItNumber() {
    return AxiosService.get<{ results: string }>(`${HOUSE_BASE_URL}/it/number`);
  }

  updatePrintPickupReceiptStatus(manifestId: string) {
    return AxiosService.put<HblEntity>(`${HOUSE_BASE_URL}/printReceiptStatus`, { manifestId });
  }

  getDefaultValues(mblId: string) {
    return AxiosService.get<
      Pick<HblEntity, 'availableDate' | 'lfd' | 'pod' | 'warehouse' | 'paymentLookup'>
    >(`${HOUSE_BASE_URL}/defaultValues`, {
      params: {
        mblId,
      },
    });
  }

  balanceDue(hblId: string, payload: { remainingPcs: number; inventoryLocation: string }) {
    return AxiosService.post(`${HOUSE_BASE_URL}/balanceCargo/${hblId}`, payload);
  }

  segregate(hblId: string, noOfCopies: number) {
    return AxiosService.post(`${HOUSE_BASE_URL}/segregate`, { hblId, noOfCopies });
  }

  unReleased(hblId: string) {
    return AxiosService.post(`${HOUSE_BASE_URL}/unrelease`, { hblId });
  }
}

export default new HouseService();
